import React, { useState, useEffect, useRef } from 'react'
import { handleRequest } from '../../../utils/httpRequest';
import { getEndpointUrl } from '../../../utils/endPoints';
import DealerSearch from './DealerSearch/DealerSearch';
import DealerMap from './DealerMap/DealerMap';
import generalController from '../../../controllers/generalController';
import { capitalCities, localStorageKeys } from '../../../utils/constants';
import formValidationController from '../../../controllers/formValidationController';
import { convertPostalToCoordinates, getCurrentBounds, getDealersInViewport, getPreviousUserlocation, getUserGeoLocation, storeUserLocation } from '../../../utils/googleMapHelper';
import { languageController } from '../../../controllers/language/languageController';
import parse from 'html-react-parser';
import { getItemOnLocalStorage, pushToGTMDataLayer, titleCase } from '../../../utils/helpers';
// @ts-ignore - gsap types not available
import { ScrollTrigger } from "gsap/all";
import Button from '../../1_Atoms/Button/Button';
import { Svg } from '../../1_Atoms/Svg/Svg';
import Modal from '../../2_Molecules/Modal/Modal';

interface DealerGeolocation {
  latitude: number;
  longitude: number;
}

interface DealerData {
  dealer_code: number;
  name: string;
  geolocation: DealerGeolocation;
  schedule_service_link?: string;
}

interface FormExtraParams {
  introductionText?: string;
  BaSALink?: string;
  BaTDLink?: string;
  GaQLink?: string;
}

interface UserLocation {
  lat: number;
  lng: number;
}

interface FindADealerProps {
  isEVonly?: boolean;
  isStepRenderType?: boolean;
  isEmbeddedOnBnP?: boolean;
  onSelectDealer?: (data: { dealer: DealerData; userPosition: UserLocation }) => void;
  formExtraParams: FormExtraParams;
  preDefinedUserLocation?: UserLocation;
  preDefinedDealerId?: string;
  dealerId?: string;
  language?: string;
  displayServiceHoursOnly?: boolean;
  isModelPreOrder?: boolean;
}

interface StatusMessage {
  text: string;
  isError: boolean;
}

interface GeolocationResult {
  success: boolean | string;
  data: any;
}

interface ApiDealerResponse {
  data: DealerData | DealerData[];
}

declare global {
  interface Window {
    mzCollapseBlock?: {
      init: (elements: NodeListOf<Element>) => void;
    };
    mzFunc?: {
      checkCookie: (name: string) => string | null;
    };
    bnp_retailer_mode?: boolean;
  }
}

const FindADealer: React.FC<FindADealerProps> = ({
  isEVonly = false,
  isStepRenderType = false,
  isEmbeddedOnBnP = false,
  onSelectDealer,
  formExtraParams,
  preDefinedUserLocation,
  preDefinedDealerId,
  dealerId,
  language,
  displayServiceHoursOnly,
  isModelPreOrder = false
}) => {
  const [dealers, setDealers] = useState<DealerData[]>([])
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [statusMessage, setStatusMessage] = useState<StatusMessage>({ text: '', isError: false })
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [initPostalKeyword, setInitPostalKeyword] = useState<string | null>(null)

  const [modalOpen, setIsModalOpen] = useState<boolean>(false)
  const [dealerData, setDealerData] = useState<DealerData | false>(false)

  const componentRef = useRef<HTMLDivElement>(null);

  const { introductionText, BaSALink, BaTDLink, GaQLink } = formExtraParams

  const [searchType, setSearchType] = useState<string>('')

	let res: GeolocationResult | any = [];

  const searchDealers = async (searchValue: string | UserLocation, searchType: string) => {
    if (!searchValue) return;

    setSearchType(searchType)

    if (errorMessage) {
      setErrorMessage('')
    }

    let dealerApi = ''
    let newBounds = null

    //retailer
    if(searchType=== 'retailer'){
      dealerApi = `${getEndpointUrl('get-dealer')}/${dealerId}`
    // For keyword search (dealer name or city name)
    }else if (searchType === 'keyword') {
      dealerApi = `${getEndpointUrl('find-dealers')}&keyword=${encodeURI(searchValue as string)}`;
    }
    // For geolocation search (lat and long)
    else if (searchType === 'geolocation') {
      const location = searchValue as UserLocation;
      // Set the passed coordinates value as center.
      map?.setCenter({
        lat: location.lat,
        lng: location.lng
      })
      // Get new bounds after setCenter
      newBounds = getCurrentBounds(map as any)
      // Get a dealer api url with some params.
      // Pass 'newBounds' to api to limit dealers based on min/max lat and long.
      dealerApi = `${getEndpointUrl('find-dealers')}&lat=${location.lat}&lng=${location.lng}&minlng=${newBounds.minLong}&maxlng=${newBounds.maxLong}&minlat=${newBounds.minLat}&maxlat=${newBounds.maxLat}`;
    } else if (searchType === 'init') {
        map?.setCenter({
          lat: 58.6348024,
          lng: -102.6680236
        })
        // Get new bounds after setCenter
        newBounds = getCurrentBounds(map as any)
    }

    if (isEVonly) {
      if(searchType==='retailer'){
        dealerApi = `${dealerApi}?ev_only=true`;
      }else{
        dealerApi = `${dealerApi}&ev_only=true`;
      }
    }

    try {
      const res = await handleRequest('get', dealerApi) as ApiDealerResponse;
      let dealerData: DealerData[];
      // check if retailer result
      if((res.data as DealerData).dealer_code){
        dealerData = [res.data as DealerData];
      }else{
        dealerData = res.data as DealerData[];
      }
      // Check if res includes dealers data.
      if (dealerData.length > 0) {
        if (searchType === 'keyword') {
          // Sort dealers alphabetically
          //dealerData.sort((a, b) => (a.name > b.name) ? 1 : -1);
          
          // Set the first most relavant dealer as center.
          map?.setCenter({
            lat: dealerData[0].geolocation.latitude,
            lng: dealerData[0].geolocation.longitude
          })
          
          // Get new bounds after setCenter
          newBounds = getCurrentBounds(map as any)
        } 

        const preferredDealer = window.mzFunc?.checkCookie('preferredDealer');
        const index = dealerData.findIndex(
          dealer => dealer.dealer_code === (preferredDealer ? Number(preferredDealer) : null)
        );

        if (index > 0) {
          // Remove the dealer from its current position
          const [preferredDealer] = dealerData.splice(index, 1);
          // Add it to the front of the array
          dealerData.unshift(preferredDealer);
        }

        setDealers(dealerData)
        
        window.mzCollapseBlock?.init(document.querySelectorAll('.mzReactForms .mz-collapse-forms'))
      } else {
        setErrorMessage(languageController.getCopy('findDealer', 'errorNoResults') || '')
      }
    } catch (err) {
      //console.log(err)
      //setErrorMessage(languageController.getCopy('findDealer', 'errorNoResults'))
      setDealers([])
    }
  }

  const geoResults = async (res: GeolocationResult) => {
	  if (res.success === true) {
	   const geoDealer = await searchDealers(res.data as any, 'geolocation')
		setStatusMessage({ isError: false, text: '' })
		// Set user geolocation in localstorage.
		storeUserLocation('geolocation', res.data as any)
		pushToGTMDataLayer({ event: 'Dealer search by current location', result: 'success', location: res.data });
	}else if(res.success === 'timeout'){
		setStatusMessage({ text: languageController.getCopy('findDealer', 'geoUnavailable') || '', isError: true });
		setTimeout(() => {
		  setStatusMessage({ isError: false, text: '' })
		}, 3000)
	  } else {
		setStatusMessage({ text: res.data, isError: true })
		pushToGTMDataLayer({ event: 'Dealer search by current location', result: 'error', location: null });
		// Remove error message after 3s
		setTimeout(() => {
		  setStatusMessage({ isError: false, text: '' })
		}, 3000)
	  }
  }

  // Search dealers based on entered search value.
  const onDealerSearchSubmit = async (dealerValue: string, postalValue: string) => {
    if (!dealerValue && !postalValue) return;

    const searchValue = dealerValue.length > 0 ? dealerValue : postalValue;

    setStatusMessage({ text: languageController.getCopy('findDealer', 'searching') || '', isError: false })

    // Check if it is a postalcode search
    if (formValidationController.validateField(searchValue, 'postalcode')) {
      const coordinates = await convertPostalToCoordinates(searchValue)
      if (coordinates) {
        await searchDealers(coordinates, 'geolocation')
        // Store user postalcode data in localstorage
        storeUserLocation('postalcode', { postalcode: searchValue, coordinates: coordinates } as any)
      }
    }
    // Otherwise, perform a keyword search
    else {
      await searchDealers(searchValue, 'keyword')
    }
    setStatusMessage({ isError: false, text: '' })
  }

  // Search dealers based on user's geolocation.
  const onGeolocationSubmit = async () => {
    const locData = await new Promise<void>( async function(resolve, reject) {
        //setTimeout(() => reject(), 10000);
        let geoCount=0;
        const checkGeoPermission = setInterval(async ()=>{
          const permissionState = await isGeoPermitted();
          if(permissionState === 'denied'){
            reject();
          }else if(permissionState === 'prompt'){
            geoCount++;
            if(geoCount>13){
              reject();
            }
          }else if(permissionState === 'granted'){
            clearInterval(checkGeoPermission);
          }
        }, 1000)
        res = await getUserGeoLocation()
        resolve();
    }).then(
      (result) => {
        geoResults(res);
      },
      (error) =>{
        res.success = 'timeout';
        geoResults(res);
      }
    );
  }

  const isGeoPermitted = (): Promise<string> => {
    return navigator.permissions.query({name:'geolocation' as PermissionName}).then(function(result) {
      // Will return ['granted', 'prompt', 'denied']
      //console.log(result.state);
      return result.state;
    });
  }

  useEffect(() => {
    // Load dealers based on what info is available after map gets loaded.
    async function handleInitLoad() {
      setStatusMessage({ text: languageController.getCopy('findDealer', 'searching') || '', isError: false })
      // Get previously set dealer data if exists.
      const prevDealer = getItemOnLocalStorage(localStorageKeys.DEALER_SELECTED_BY_USER) as DealerData | null;
      // Get previously set user location data if exists.
      const prevUserLocationData = getPreviousUserlocation();

      if(typeof window.mzFunc?.checkCookie === 'function'){
        if(window.mzFunc?.checkCookie('preferredDealer') !== ''){
          if(!dealerId) dealerId = window.mzFunc?.checkCookie('preferredDealer') || undefined
        }
      }
      //realailer location
      if(dealerId){
        await searchDealers(dealerId, 'retailer')
      // If it is a step render type AND userLocation is already defined in URL Params.
      }else if (isStepRenderType && preDefinedUserLocation) {
        await searchDealers({ lat: preDefinedUserLocation.lat, lng: preDefinedUserLocation.lng }, 'geolocation')
      }
      // If it is a step render type AND dealer was chosen before on the site.
      else if (prevDealer) {
        await searchDealers({ lat: prevDealer.geolocation.latitude, lng: prevDealer.geolocation.longitude }, 'geolocation')
      }
      // If user location already exists in localstorage (geolocation or postalcode)
      else if (prevUserLocationData) {
        // Geolocation
        if (prevUserLocationData.type === 'geolocation') {
          await searchDealers(prevUserLocationData.value, 'geolocation')
        }
        // Postalcode
        else if (prevUserLocationData.type === 'postalcode') {
          const value = prevUserLocationData.value as any;
          await searchDealers(value.coordinates, 'geolocation')
          // Set postalcode as an init input value.
          setInitPostalKeyword(value.postalcode)
        }
      }
      // Otherwise, use capital city of user's province
      else {
        const province = generalController.getMetatagValue('province');
        if (province) {
          const capitalData = capitalCities[province as keyof typeof capitalCities];
          if (capitalData) {
             await searchDealers(capitalData.location, 'init')
          }
        }
      }

      setStatusMessage({ isError: false, text: '' })
    }

    if (map) {
      ScrollTrigger.create({
        trigger: componentRef.current,
        once: true,
        start: "-200% top",
        onEnter: () => {
          handleInitLoad();
        },
      });
    }
  }, [map, dealerId, preDefinedDealerId])

 useEffect(() => {
    const heroContainer = document.querySelector('.form-hero-container');
    const heroEl = document.getElementById('mz-hero');
    if (!heroContainer || !heroEl || heroEl.parentElement === heroContainer) return;

    const originalParentEl = heroEl.parentElement;
    heroEl.classList.remove('visually-hidden');
    heroContainer.appendChild(heroEl);

    // Restore the hero block when the form is unmounted.
    return () => {
      if (originalParentEl) originalParentEl.appendChild(heroEl);
      heroEl.classList.add('visually-hidden');
    };
  }, [])

  return (
    <>
    <div className="form-hero-container" />
    <div className="mz-find-a-dealer" data-mz-form-type="FindADealer" ref={componentRef}>
      {introductionText && (
        <div className="mz-find-a-dealer__intro-text mz-container">
          <div className="mz-full-width">
            {parse(introductionText)}
          </div>
        </div>
      )}
      {!window.bnp_retailer_mode &&
          <DealerSearch
            // initDealerKeyword={initDealerKeyword}
            initPostalKeyword={initPostalKeyword}
            onSubmit={onDealerSearchSubmit}
            onGeolocationSubmit={() => onGeolocationSubmit()}
            statusMessage={statusMessage}
            errorMessage={errorMessage}
          />
      }
      <DealerMap
        dealers={dealers}
        searchDealers={searchDealers}
        setMap={setMap}
        map={map}
        setIsMapLoaded={setIsMapLoaded}
        isStepRenderType={isStepRenderType}
        isEmbeddedOnBnP={isEmbeddedOnBnP}
        onSelectDealer={onSelectDealer}
        BaSALink={BaSALink}
        BaTDLink={BaTDLink}
        GaQLink={GaQLink}
        currentLang={generalController.detectLanguage()}
        preDefinedDealerId={preDefinedDealerId}
        language={language}
        displayServiceHoursOnly={displayServiceHoursOnly}
        setIsModalOpen={setIsModalOpen}
        setDealerData={setDealerData}
        isModelPreOrder={isModelPreOrder}
        searchType={searchType}
      />
      <Modal
          className="mz-modal--non-sticky-top mz-modal-fifty-fifty"
          isOpen={modalOpen}
          onClose={() => {
            document.querySelector('body')?.classList.remove('disabled')
            const reactRoot = document.querySelector('.mz-react-forms-root') as HTMLElement;
            if (reactRoot) reactRoot.style.zIndex = '1';
            setIsModalOpen(false);
          }}
        >
          <div className="mz-modal__text-container">
              <h3 className="text--center">{languageController.getCopy('findDealer', 'modalServiceTitle') || ''}</h3>
              <div className="mz-spacer mz-spacer-s"></div>
              <p className="text--center">{(languageController.getCopy('findDealer', 'modalServiceContent') || '').replace('|dealer|', titleCase(dealerData ? (dealerData as DealerData).name : '') || '')}</p>
              <div className="mz-spacer mz-spacer-s"></div>
              <div className="mz-container-flex">
              <Button
                    label={(languageController.getCopy('findDealer', 'modalServiceButton') || '').replace('|dealer|', dealerData ? (dealerData as DealerData).name : '')}
                    className="mz-btn--red"
                      onClick={(e) => {
                        const reactRoot = document.querySelector('.mz-react-forms-root') as HTMLElement;
                        if (reactRoot) reactRoot.style.zIndex = '1';
                        document.querySelector('body')?.classList.remove('disabled')
                        setIsModalOpen(false);
                        document.querySelector('body')?.classList.remove('disabled');
                        if (dealerData && (dealerData as DealerData).schedule_service_link) {
                          window.open((dealerData as DealerData).schedule_service_link, '_blank')?.focus();
                        }
                        //window.location.href=dealerData.schedule_service_link;
                    }}
                      data-menu-name="Select dealer"
                      data-form-name="Find a dealer"
                      data-form-event="Book Service"
                    />
                </div>
            </div>
        </Modal>
    </div>
    </>
  )
}

export default FindADealer
